import React, { Fragment } from 'react';
import { CMSLink } from '@/components/cms-link';
import escapeHTML from 'escape-html';

// import { Highlight } from '../../Highlight'

import { RichTextUpload } from './upload';
import { Video } from './video';

interface Node {
  type: string;
  text: string;
  value?: {
    url: string;
    alt: string;
  };
  children?: Node[];
  url?: string;
  [key: string]: unknown;
  newTab?: boolean;
  textAlign?: 'left' | 'right' | 'center';
}

export type CustomRenderers = Record<
  string,
  (args: {
    node: Node;
    Serialize: SerializeFunction;
    index: number;
  }) => JSX.Element
>;

type SerializeFunction = React.FC<{
  content?: Node[];
  customRenderers?: CustomRenderers;
  isPost?: boolean;
}>;

const isText = (value: Node): boolean =>
  typeof value === 'object' && value !== null && typeof value.text === 'string';

const Heading = ({
  level,
  textAlign = 'left',
  children,
  ...props
}: {
  level: number;
  textAlign?: string;
  children: React.ReactNode;
}) => {
  const Tag = `h${level}`;
  return React.createElement(
    Tag,
    {
      className: `text-brandAlt-950 font-serif text-${textAlign}`,
      ...props,
    },
    children,
  );
};

export const Serialize: SerializeFunction = ({
  content,
  customRenderers,
  isPost,
}) => {
  return (
    <>
      {content?.map((node, i) => {
        // console.log('aaa',isText(node), node)
        if (isText(node)) {
          let text = (
            <span dangerouslySetInnerHTML={{ __html: escapeHTML(node.text) }} />
          );

          if (node.bold) {
            text = <strong key={i}>{text}</strong>;
          }

          if (node.code) {
            // default
            // text = <code key={i}>{text}</code>;
            const iframe = (
              <div
                id="custom-iframe"
                className="iframe-background"
                dangerouslySetInnerHTML={{
                  __html: node.text,
                }}
              />
            );
            text = iframe;
          }

          if (node.italic) {
            text = <em key={i}>{text}</em>;
          }

          if (node.underline) {
            text = (
              <span style={{ textDecoration: 'underline' }} key={i}>
                {text}
              </span>
            );
            // text = <Highlight key={i} {...node} />
          }

          if (node.strikethrough) {
            text = (
              <span style={{ textDecoration: 'line-through' }} key={i}>
                {text}
              </span>
            );
          }

          return <Fragment key={i}>{text}</Fragment>;
        }

        if (!node) {
          return null;
        }

        if (
          customRenderers?.[node.type] &&
          typeof customRenderers[node.type] === 'function'
        ) {
          return (
            customRenderers?.[node.type]?.({ node, Serialize, index: i }) ??
            null
          );
        }

        switch (node.type) {
          case 'br':
            return <br key={i} />;
          case 'h1':
            return (
              <Heading key={i} level={1} textAlign={node.textAlign}>
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </Heading>
            );
          case 'h2':
            return (
              <Heading key={i} level={2} textAlign={node.textAlign}>
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </Heading>
            );
          case 'h3':
            return (
              <Heading key={i} level={3} textAlign={node.textAlign}>
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </Heading>
            );
          case 'quote':
            return (
              <blockquote key={i}>
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={i} className="list-none">
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} className="list-decimal">
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </ol>
            );
          case 'li':
            return (
              <li key={i} className="list-disc">
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </li>
            );
          case 'link':
            return (
              <CMSLink
                key={i}
                type={node.linkType === 'internal' ? 'reference' : 'custom'}
                url={node.url}
                // reference={node.doc as Reference}
                newTab={node?.newTab}
              >
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </CMSLink>
            );

          case 'upload': {
            return <RichTextUpload key={i} node={node} isPost={isPost} />;
          }

          // case 'label':
          //   return (
          //     <Label key={i}>
          //       <Serialize
          //         content={node.children}
          //         customRenderers={customRenderers}
          //       />
          //     </Label>
          //   );

          // case 'large-body': {
          //   return (
          //     <LargeBody key={i}>
          //       <Serialize
          //         content={node.children}
          //         customRenderers={customRenderers}
          //       />
          //     </LargeBody>
          //   );
          // }
          case '14px':
            return (
              <p
                key={i}
                className={`text-sm ${
                  node.textAlign === 'center'
                    ? // additional padding to make center aligned text stands out more
                      'px-6 text-center md:px-10 lg:px-12'
                    : `text-${node.textAlign ?? 'left'}`
                }`}
              >
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </p>
            );
          case '16px':
            return (
              <p
                key={i}
                className={`text-base ${
                  node.textAlign === 'center'
                    ? // additional padding to make center aligned text stands out more
                      'px-6 text-center md:px-10 lg:px-12'
                    : `text-${node.textAlign ?? 'left'}`
                }`}
              >
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </p>
            );
          case '18px':
            return (
              <p
                key={i}
                className={`text-lg ${
                  node.textAlign === 'center'
                    ? // additional padding to make center aligned text stands out more
                      'px-6 text-center md:px-10 lg:px-12'
                    : `text-${node.textAlign ?? 'left'}`
                }`}
              >
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </p>
            );
          case 'video': {
            const { source, id: videoID } = node;

            if (source === 'vimeo' || source === 'youtube') {
              return <Video key={i} platform={source} id={videoID as string} />;
            }

            return null;
          }

          default:
            return (
              <p
                key={i}
                className={`${
                  node.textAlign === 'center'
                    ? // additional padding to make center aligned text stands out more
                      'px-6 text-center md:px-10 lg:px-12'
                    : `text-${node.textAlign ?? 'left'}`
                }`}
              >
                <Serialize
                  content={node.children}
                  customRenderers={customRenderers}
                />
              </p>
            );
        }
      })}
    </>
  );
};
